import { FC, useEffect } from 'react';
import { ReportEditButton } from '@/features/edit';
import { reportQueries, useReportStore } from '@/entities/report';
import { AnalysisTab, ErrataBox, FeedbackTab } from '../../common';
import { ContentsInfo } from './contents-info';
import { KvResultContainer } from '@/shared/ui';
import { useReportEventListener } from '@/shared/hooks/report/use-report-event-listener';

interface ReportContentsViewerProps {
    evlId?: number;
    stntId?: string;
    setsId?: string;
    selectedThumbContent?: EvlInfo;
    selectedContentsNumber: number;
    selectedSubTab: string;
    isStudent?: boolean;
    onRefetch?: () => void;
}

export const ReportStntContentsViewer: FC<ReportContentsViewerProps> = ({
                                                                            evlId = 0,
                                                                            stntId = '',
                                                                            setsId = '',
                                                                            selectedThumbContent,
                                                                            selectedContentsNumber,
                                                                            selectedSubTab,
                                                                            isStudent,
                                                                            onRefetch,
                                                                        }) => {
    const submAt = selectedThumbContent?.mdulEvlInfo?.submAt;
    // const subMitAnw = selectedThumbContent?.mdulEvlInfo?.subMitAnw;
    const articleType = selectedThumbContent?.mdulEvlInfo?.articleType;
    const errata = selectedThumbContent?.mdulEvlInfo?.errata;
    const evlResultId = selectedThumbContent?.mdulEvlInfo?.evlResultId ?? 0;
    const evlIemId = selectedThumbContent?.mdulItemInfo?.evlIemId ?? '';
    const subId = selectedThumbContent?.mdulEvlInfo?.subId ?? 0;
    const fdbDc = isStudent
        ? (selectedThumbContent?.mdulEvlInfo?.fdbInfo?.fdbDc ?? '')
        : (selectedThumbContent?.mdulEvlInfo?.fdbDc ?? '');
    const analysis = selectedThumbContent?.mdulItemInfo?.myAnalysisInfo;

    const enabled = selectedSubTab === 'student' && evlResultId > 0;
    const { data: result, refetch } = reportQueries.share.getStntEvalResutInfo(
        {
            evlResultId: evlResultId,
            evlIemId: evlIemId,
            subId: subId,
        },
        enabled
    );
    const modified = result
        ? {
            ...result,
            errata: errata!,
        }
        : undefined;

    // 리포트 이벤트리스너 커스텀훅
    const { eventListener } = useReportEventListener(modified);

    // 학생 결과 리패치
    const resultInfoRefetchHandler = () => {
        refetch();
    };

    return (
        <div className="tbl-con-right">
            <div className="table-div">
                {selectedSubTab === 'contents' ? (
                    <ContentsInfo
                        contentsNum={selectedContentsNumber}
                        selectedThumbContent={selectedThumbContent}
                        submAt={submAt}
                        articleType={articleType}
                    />
                ) : (
                    <StntInfo
                        contentsNum={selectedContentsNumber}
                        selectedThumbContent={selectedThumbContent}
                        submAt={submAt}
                        articleType={articleType}
                        errata={errata}
                        isStudent={isStudent}
                        onRefetch={resultInfoRefetchHandler}
                    />
                )}
                {/* 콘텐츠 정보 */}
                {selectedSubTab === 'contents' && (
                    <>
                        <div className="cont-wrap">
                            <KvResultContainer
                                key={evlIemId}
                                articleId={evlIemId}
                                subId={subId}
                                // style={{ width: '100%' }}
                                isHand={'N'}
                                setId={setsId}
                            />
                        </div>
                        {/* 우리반 분석 */}
                        <AnalysisTab
                            title={isStudent ? '내 분석' : '학생 분석'}
                            analysis={analysis}
                            isStnts={false}
                        />
                    </>
                )}
                {/* 학생/내 답안 */}
                {selectedSubTab === 'student' && (
                    <>
                        <div className="cont-wrap">
                            <KvResultContainer
                                key={evlIemId}
                                articleId={evlIemId}
                                subId={subId}
                                errata={errata}
                                // style={{ width: '100%' }}
                                isHand={'N'}
                                isMark="Y"
                                setId={setsId}
                                eventListener={eventListener}
                            />
                        </div>
                        <FeedbackTab
                            evlId={evlId}
                            evlIemId={evlIemId}
                            subId={subId}
                            fdbDc={fdbDc}
                            stntId={stntId}
                            isStudent={isStudent}
                            onRefetch={onRefetch}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

const StntInfo: FC<ReportContentsInfoProps> = ({
                                                   contentsNum,
                                                   selectedThumbContent,
                                                   submAt,
                                                   articleType,
                                                   errata,
                                                   isStudent,
                                                   onRefetch,
                                               }) => {
    const { stntReport } = useReportStore();
    const targetId = stntReport.targetId ?? 0;
    const stntId = stntReport.stntId ?? '';
    const evlIemId = selectedThumbContent?.mdulEvlInfo?.evlIemId ?? '';
    const subId = selectedThumbContent?.mdulEvlInfo?.subId ?? 0;
    const mrkTy = selectedThumbContent?.mdulEvlInfo?.mrkTy ?? 0;
    const subStnt = selectedThumbContent?.mdulEvlInfo?.submAt;

    const time = submAt === 'Y' ? (selectedThumbContent?.mdulEvlInfo?.solvSecAvr ?? '-') : '-';
    const totalScore = selectedThumbContent?.mdulItemInfo?.evlIemScr ?? 0;
    const myScore = selectedThumbContent?.mdulEvlInfo?.evlIemScrResult ?? 0;

    useEffect(() => {
        onRefetch();
    }, [myScore]);

    return (
        <div className="cont-info">
            <strong className="title">[{contentsNum}번 콘텐츠] 답안</strong>
            <dl>
                <dt>풀이 시간</dt>
                <dd>{time}</dd>
                <dt>점수</dt>
                {mrkTy === 2 &&
                subStnt !== 'Y' &&
                (articleType === 20 || errata === null || errata === 4) ? (
                    <dd>-</dd>
                ) : (
                    <dd>
                        {isStudent ? (
                            myScore
                        ) : (
                            <>
                                {submAt === 'Y' ? myScore : '-'}/{totalScore}
                                <br />
                                <ReportEditButton
                                    myScore={myScore}
                                    totalScore={totalScore}
                                    submAt={submAt}
                                    targetId={targetId}
                                    stntId={stntId}
                                    evlIemId={evlIemId}
                                    subId={subId}
                                    type="stnt"
                                />
                            </>
                        )}
                    </dd>
                )}
                <dt>정오표</dt>
                <dd>
                    <ErrataBox articleType={articleType} submAt={submAt} errata={errata} />
                </dd>
            </dl>
        </div>
    );
};
